import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CHART_FONT, useChartTheme } from "@/features/charts/theme";
import { ChartTooltip } from "@/features/charts/tooltip";
import { useDateFnsLocale, useTranslation } from "@/features/utils/i18n";
import { ResponsiveLine, type LineCustomSvgLayerProps } from "@nivo/line";
import { format, startOfDay, subDays } from "date-fns";
import { useEffect, useRef, useState } from "react";

interface ActivityChartProps {
  /** Per-day counts keyed `YYYY-MM-DD`, in the viewer's own time zone. */
  data: Array<{ date: string; count: number }>;
  /** How many days the chart shows, today included. */
  days: number;
}

type ActivitySeries = { id: string; data: Array<{ x: Date; y: number }> };
type LayerProps = LineCustomSvgLayerProps<ActivitySeries>;

const HALF_DAY_MS = 12 * 60 * 60 * 1000;

export function ActivityChart({ data, days }: ActivityChartProps) {
  const { t } = useTranslation("dashboard");
  const locale = useDateFnsLocale();
  const { theme, tokens } = useChartTheme();
  const isFirstRender = useRef(true);
  // The day under the pointer, and where its column sits in the plot.
  const [hovered, setHovered] = useState<{ index: number; x: number } | null>(
    null,
  );

  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  // Every day of the range, at local midnight, oldest first. Days are built
  // and keyed in local time throughout: mixing in `toISOString` (UTC) or
  // `new Date("YYYY-MM-DD")` (UTC midnight) is what used to put a count on the
  // wrong day.
  const today = startOfDay(new Date());
  const counts = new Map(data.map((d) => [d.date, d.count]));
  let cumulative = 0;
  const filledData = Array.from({ length: Math.max(days, 1) }, (_, i) => {
    const date = subDays(today, Math.max(days, 1) - 1 - i);
    const count = counts.get(format(date, "yyyy-MM-dd")) ?? 0;
    cumulative += count;
    return { date, count, cumulative };
  });

  const lineData = filledData.map((day) => ({ x: day.date, y: day.cumulative }));

  // The daily bars and the running total, in the first two colours of the brand
  // gradient — the order every chart takes them in.
  const [barColor, lineColor] = tokens.series;
  const tickStyle = { fill: tokens.text, fontSize: 11, fontFamily: CHART_FONT };

  // Get max values for scaling
  const maxDailyCount = Math.max(...filledData.map((d) => d.count), 1);
  const totalCount = cumulative;

  // Both axes count sessions, so their ticks must be whole numbers — and both
  // ladders share the same number of intervals so the daily labels on the left
  // line up with the grid lines drawn from the running total's scale.
  const tickCount = Math.min(5, Math.max(1, maxDailyCount, totalCount));
  const buildTicks = (max: number) => {
    const rawStep = Math.max(max, 1) / tickCount;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const niceStep =
      [1, 2, 2.5, 5, 10]
        .map((m) => m * magnitude)
        .find((candidate) => candidate >= rawStep) ?? rawStep;
    const step = Math.max(1, Math.round(niceStep), Math.ceil(rawStep));
    return Array.from({ length: tickCount + 1 }, (_, i) => i * step);
  };

  const cumulativeTicks = buildTicks(totalCount);
  const cumulativeMax = cumulativeTicks[cumulativeTicks.length - 1];
  const dailyTicks = buildTicks(maxDailyCount);
  const dailyMax = dailyTicks[dailyTicks.length - 1];

  // Leave room for the widest label on each side. The day's count reads on the
  // left, next to the bars it measures; the running total on the right, where a
  // line that only climbs ends up.
  const labelWidth = (value: number) => String(value).length * 8;
  const margin = {
    top: 20,
    right: 14 + labelWidth(cumulativeMax),
    bottom: 40,
    left: 14 + labelWidth(dailyMax),
  };

  // Each day owns a column of the plot, its bar and its point in the middle:
  // the domain runs half a day past the first and the last day so their
  // columns are as wide as the others instead of being cut in half at the edge.
  const columnWidth = (innerWidth: number) => innerWidth / filledData.length;

  const hoveredDay = hovered ? filledData[hovered.index] : null;

  return (
    // Grows with its row, which the classification card beside it sets: a
    // fixed-height chart left a blank band at the bottom of the card.
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle>{t("activity.title")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col">
        <div
          className="relative min-h-[300px] flex-1"
          onPointerLeave={() => setHovered(null)}
        >
          {filledData.length > 0 ? (
            <ResponsiveLine<ActivitySeries>
              data={[
                {
                  id: "cumulative",
                  data: lineData,
                },
              ]}
              margin={margin}
              xScale={{
                type: "time",
                format: "native",
                useUTC: false,
                min: new Date(filledData[0].date.getTime() - HALF_DAY_MS),
                max: new Date(today.getTime() + HALF_DAY_MS),
              }}
              yScale={{
                type: "linear",
                min: 0,
                max: cumulativeMax,
              }}
              curve="monotoneX"
              colors={lineColor}
              lineWidth={2.5}
              pointSize={6}
              pointColor={tokens.surface}
              pointBorderWidth={2}
              pointBorderColor={lineColor}
              enableArea={false}
              enableGridX={false}
              enableGridY={true}
              gridYValues={cumulativeTicks}
              axisTop={null}
              axisRight={{
                tickSize: 0,
                tickPadding: 6,
                tickValues: cumulativeTicks,
                format: (value) => (value === 0 ? "" : value),
              }}
              axisBottom={null}
              axisLeft={null}
              animate={isFirstRender.current}
              motionConfig="gentle"
              // Hovering is ours (the last layer): nivo's own picks the point
              // of the running total nearest the pointer, which near the bars
              // is an earlier day's — the tooltip then named another day.
              isInteractive={false}
              theme={theme}
              layers={[
                "grid",
                "axes",
                // The hovered day's column, then the daily bars
                ({ xScale, innerHeight, innerWidth }: LayerProps) => {
                  const column = columnWidth(innerWidth);
                  const barWidth = Math.max(6, Math.min(20, column * 0.4));

                  return (
                    <g style={{ pointerEvents: "none" }}>
                      {hovered ? (
                        <rect
                          x={hovered.x - column / 2}
                          y={0}
                          width={column}
                          height={innerHeight}
                          fill={tokens.foreground}
                          fillOpacity={0.06}
                        />
                      ) : null}
                      {filledData.map((day, index) => {
                        if (day.count === 0) return null;
                        const x = xScale(day.date);
                        // Scale bars on the left Y-axis ladder
                        const barHeight = (day.count / dailyMax) * innerHeight;

                        return (
                          <rect
                            key={day.date.getTime()}
                            x={x - barWidth / 2}
                            y={innerHeight - barHeight}
                            width={barWidth}
                            height={barHeight}
                            fill={barColor}
                            fillOpacity={
                              hovered && hovered.index !== index ? 0.45 : 1
                            }
                            rx={3}
                            ry={3}
                          />
                        );
                      })}
                      {/* Custom left axis ticks for daily scale */}
                      {dailyTicks.map((value) => {
                        if (value === 0) return null; // Hide zero
                        const y = innerHeight - (value / dailyMax) * innerHeight;
                        return (
                          <text
                            key={`left-tick-${value}`}
                            x={-6}
                            y={y}
                            textAnchor="end"
                            dominantBaseline="middle"
                            style={tickStyle}
                          >
                            {value}
                          </text>
                        );
                      })}
                    </g>
                  );
                },
                // Custom bottom axis with different font weights
                ({ xScale, innerHeight }: LayerProps) => {
                  const y = innerHeight + 20;

                  return (
                    <g transform={`translate(0, ${y})`}>
                      {filledData.map(({ date }) => {
                        const dayNum = date.getDate();

                        let label = "";
                        let fontWeight = 400;

                        if (dayNum === 1) {
                          label = format(date, "LLL", { locale });
                          fontWeight = 600;
                        } else if (
                          dayNum === 8 ||
                          dayNum === 15 ||
                          dayNum === 22 ||
                          dayNum === 29
                        ) {
                          label = dayNum.toString();
                          fontWeight = 400;
                        }

                        if (!label) return null;

                        return (
                          <text
                            key={date.getTime()}
                            x={xScale(date)}
                            y={0}
                            textAnchor="middle"
                            dominantBaseline="hanging"
                            style={{ ...tickStyle, fontWeight }}
                          >
                            {label}
                          </text>
                        );
                      })}
                    </g>
                  );
                },
                "lines",
                "points",
                // The hovered day's point, then one hover target per day: the
                // whole height of its column, so the bar, the point and the
                // empty space between them all name the same day.
                ({ xScale, yScale, innerHeight, innerWidth }: LayerProps) => {
                  const column = columnWidth(innerWidth);
                  return (
                    <g>
                      {hoveredDay && hovered ? (
                        <circle
                          cx={hovered.x}
                          cy={yScale(hoveredDay.cumulative)}
                          r={5}
                          fill={lineColor}
                          stroke={tokens.surface}
                          strokeWidth={2}
                          style={{ pointerEvents: "none" }}
                        />
                      ) : null}
                      {filledData.map((day, index) => {
                        const x = xScale(day.date);
                        return (
                          <rect
                            key={day.date.getTime()}
                            x={x - column / 2}
                            y={0}
                            width={column}
                            height={innerHeight}
                            fill="transparent"
                            onPointerEnter={() => setHovered({ index, x })}
                          />
                        );
                      })}
                    </g>
                  );
                },
              ]}
            />
          ) : (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              {t("activity.noActivity")}
            </div>
          )}
          {hoveredDay && hovered ? (
            <div
              className="pointer-events-none absolute z-10"
              style={{
                left: margin.left + hovered.x,
                top: margin.top,
                // Beside the column, on the side with room for it.
                transform:
                  hovered.index >= filledData.length / 2
                    ? "translateX(calc(-100% - 16px))"
                    : "translateX(16px)",
              }}
            >
              <ChartTooltip
                title={format(hoveredDay.date, "EEEE d MMMM", { locale })}
                rows={[
                  {
                    key: "daily",
                    color: barColor,
                    label: t("activity.daily"),
                    value: hoveredDay.count,
                  },
                  {
                    key: "cumulative",
                    color: lineColor,
                    label: t("activity.cumulative"),
                    value: hoveredDay.cumulative,
                  },
                ]}
              />
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
